import React, { FC, ReactNode, useState } from "react"
import { widgetMatchesSearch } from "components/dashboard/grid/helpers"
import { WidgetLayout } from "components/dashboard/widgets/shared/widgetLayout"
import { Tooltip } from "components/core/tooltip"
import { useUiContext } from "components/providers/uiProvider"
import { Widget } from "domainModels/widget"
import { useCurrentDashboard, useIsDashboardOwner } from "reactQuery/dashboard"
import { useDeleteWidget } from "reactQuery/widget"
import { WidgetConfig } from "components/dashboard/grid/config"
import { Button } from "components/core/button"
import { Modal } from "components/core/modal/modal"

interface Props {
  widget: Widget
  widgetConfig: WidgetConfig
}

export const BuiltInWidget: FC<Props> = ({ widget, widgetConfig }) => {
  const { data: dashboard } = useCurrentDashboard()
  const { mutateAsync: deleteWidget } = useDeleteWidget()
  const { value } = useUiContext()
  const { searchQuery } = value
  const isDashboardOwner = useIsDashboardOwner()
  const isBlurred =
    searchQuery.length > 0 &&
    !widgetMatchesSearch({ name: widget.information.name, keywords: widget.information.keywords, searchQuery }) &&
    !widget.information.markdown?.toLowerCase().includes(searchQuery)

  const onRemove = async () => {
    if (widget && dashboard?.id) {
      await deleteWidget({ widget, dashboardId: dashboard.id })
    }
  }

  return (
    <WidgetLayout
      layout={widget.layout}
      isLocked={widget.layout?.xxl.static}
      isBlurred={isBlurred}
      isDashboardOwner={isDashboardOwner}
      onRemove={onRemove}
    >
      {widgetConfig.renderPreview ? (
        <>
          <div>
            <Tooltip content={widget.information.description}>
              <div className="relative z-10 inline-flex items-center mb-3">
                <BuiltInWidgetIcon icon={widgetConfig.icon} />
                <h2 className="ml-2 inline-flex items-center font-medium text-gray-900 dark:text-gray-200">
                  {widget.information.name}
                </h2>
              </div>
            </Tooltip>
          </div>
          {widgetConfig.renderPreview(widget)}
        </>
      ) : (
        <BuiltInWidgetWithoutPreview widget={widget} widgetConfig={widgetConfig} />
      )}
    </WidgetLayout>
  )
}

const BuiltInWidgetIcon: FC<{ icon: ReactNode }> = ({ icon }) => (
  <div className="w-6 h-6 -ml-0.5 rounded-md transition-colors text-indigo-800 dark:text-indigo-400">{icon}</div>
)

const BuiltInWidgetWithoutPreview: FC<Props> = ({ widget, widgetConfig }) => {
  const [showModal, setShowModal] = useState(false)

  return (
    <div className="h-full w-full flex items-center justify-center">
      <Button
        onClick={() => setShowModal(true)}
        variant="naked"
        className="z-10 text-base flex-wrap justify-center gap-2 !text-gray-700 dark:!text-gray-300 hover:scale-105"
      >
        <BuiltInWidgetIcon icon={widgetConfig.icon} />
        {widget.information.name}
      </Button>

      <Modal show={showModal} className="md:max-w-7xl" onClose={() => setShowModal(false)}>
        {widgetConfig.renderModal({ widget })}
      </Modal>
    </div>
  )
}
